/**
 * Where a new game starts, when the link does not say.
 *
 * The first time, the player is asked whether to start where they are. Yes
 * uses the device's own location (GPS on a phone). No — or a location that
 * cannot be had — falls back to where their connection is, as Cloudflare
 * reckons it (`/rt/where`, a city or so). Failing that too, the caller opens
 * the world map to let them choose where to start.
 *
 * The answer is remembered, so the question is asked once; afterwards a
 * start uses the same way without a word. A link with coordinates — from
 * smartmaps, shared, or the game's own address on a reload — never comes
 * here: it starts exactly where it says.
 */

import { locate } from '../map/geolocate.js';

export type StartSource = 'device' | 'network';

export interface StartPlace {
  lat: number;
  lon: number;
  source: StartSource;
  /** "Borås, SE", for a network estimate, when known. */
  label?: string;
}

const KEY = 'wsg.start';

function load(): StartSource | undefined {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'device' || v === 'network' ? v : undefined;
  } catch { return undefined; }
}
function save(v: StartSource): void {
  try { localStorage.setItem(KEY, v); } catch { /* private mode */ }
}

/** Whether the link names a place to start, and so decides for itself. */
export function linkHasStart(search: string): boolean {
  const p = new URLSearchParams(search);
  return (p.get('lat') ?? '').trim() !== '' && (p.get('lon') ?? '').trim() !== '';
}

/** Where the connection is, per the realtime Worker; undefined when it cannot say. */
export async function networkPlace(url = '/rt/where', timeoutMs = 4000): Promise<StartPlace | undefined> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, { signal: controller.signal, cache: 'no-store' });
    if (!response.ok) return undefined;
    const body = await response.json() as { lat?: unknown; lon?: unknown; city?: unknown; country?: unknown };
    const lat = Number(body.lat), lon = Number(body.lon);
    if (!Number.isFinite(lat) || !Number.isFinite(lon) || Math.abs(lat) > 85 || Math.abs(lon) > 180) return undefined;
    const label = [body.city, body.country].filter((v): v is string => typeof v === 'string' && v !== '').join(', ');
    return { lat, lon, source: 'network', label: label === '' ? undefined : label };
  } catch {
    return undefined;
  } finally {
    clearTimeout(timer);
  }
}

const CSS = `
.wsg-start { position: fixed; inset: 0; z-index: 30; display: flex; align-items: center; justify-content: center;
  background: rgba(30, 28, 40, 0.28); }
.wsg-start .card { max-width: min(380px, calc(100vw - 32px)); padding: 20px 22px; text-align: center; }
.wsg-start h2 { margin: 0 0 8px; font: 800 19px system-ui, sans-serif; }
.wsg-start p { margin: 0 0 16px; font: 500 14px/1.45 system-ui, sans-serif; color: #4a4f58; }
.wsg-start button { display: block; width: 100%; margin-top: 8px; border: 0; border-radius: 11px; padding: 12px 14px;
  font: 800 15px system-ui, sans-serif; cursor: pointer; }
.wsg-start .yes { background: #e2473a; color: #fff; }
.wsg-start .no { background: #e6dcc9; color: #4a4f58; }
`;

/** Ask, once: start where you are? Resolves true for yes. */
export function askToUseLocation(parent: HTMLElement): Promise<boolean> {
  if (document.getElementById('wsg-start-style') === null) {
    const style = document.createElement('style');
    style.id = 'wsg-start-style';
    style.textContent = CSS;
    document.head.appendChild(style);
  }
  return new Promise((resolve) => {
    const root = document.createElement('div');
    root.className = 'wsg-start';
    root.innerHTML = `<div class="wsg-panel card">
      <h2>Start where you are?</h2>
      <p>The game starts you on the ground where you are now.</p>
      <button class="yes">📍 Use my location</button>
      <button class="no">Not now</button>
    </div>`;
    const done = (yes: boolean) => { root.remove(); resolve(yes); };
    root.querySelector('.yes')!.addEventListener('click', () => done(true));
    root.querySelector('.no')!.addEventListener('click', () => done(false));
    parent.appendChild(root);
  });
}

/**
 * Where to start: asked the first time, then as answered. Undefined when
 * neither the device nor the connection can say — the caller lets the player
 * choose on the map.
 */
export async function findStart(parent: HTMLElement): Promise<StartPlace | undefined> {
  let choice = load();
  if (choice === undefined) {
    choice = (await askToUseLocation(parent)) ? 'device' : 'network';
    save(choice);
  }
  if (choice === 'device') {
    try {
      const fix = await locate();
      return { lat: fix.lat, lon: fix.lon, source: 'device' };
    } catch {
      // Denied, or no fix: the connection's estimate instead.
    }
  }
  return networkPlace();
}
